import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["setIsAdvanced", "isAdvanced", "setIdentifier", "identifier", "withMarginTop"];
import React, { memo, useState, useMemo, useCallback } from 'react';
import { createUseStyles, Icon, Text, Switch, Advice, RadioGroup, Popover, Markdown, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import { ADVANCED_IMPORT_IDENTIFIERS } from "../../../constants";
import styles from "./styles";
const images = {
  0: 'imgImportsName',
  1: 'imgImportsFmId',
  2: 'imgImportsExtId'
};
const useStyles = createUseStyles(styles, 'IdentifierPicker');
var _ref3 = /*#__PURE__*/React.createElement(Icon, {
  name: "relationFields"
});
const IdentifierPicker = /*#__PURE__*/memo(_ref => {
  let {
      setIsAdvanced,
      isAdvanced,
      setIdentifier,
      identifier,
      withMarginTop
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const classes = useStyles();
  const theme = useTheme();
  const {
    t
  } = useWeb5Context();
  const [popoverVisible, setPopoverVisible] = useState('');
  const onChangeAdvanced = useCallback(bool => {
    setIsAdvanced && setIsAdvanced(bool);
  }, [setIsAdvanced]);
  const getPopoverContent = useCallback(_ref2 => {
    let {
      id,
      title,
      text
    } = _ref2;
    const imgName = images[id];
    let imageSrc = null;
    if (props[imgName]) imageSrc = props[imgName];
    return /*#__PURE__*/React.createElement("div", {
      className: classes.popoverContainer
    }, imageSrc && /*#__PURE__*/React.createElement("img", {
      className: classes.popoverImage,
      src: imageSrc,
      alt: "import"
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.popoverTextContainer
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.popoverText
    }, /*#__PURE__*/React.createElement(Text, {
      type: "h6",
      bold: true,
      color: theme.colors.textLight.primary
    }, title), /*#__PURE__*/React.createElement(Markdown, {
      className: classes.popoverTextMarkdown,
      content: text
    }))));
  }, [classes, props, theme]);
  const popoverDefaultProps = useMemo(() => {
    return {
      className: classes.popover,
      placement: 'top',
      trigger: ['hover']
    };
  }, [classes]);
  const onMouseOverOrOut = useCallback(id => () => {
    setPopoverVisible(id || '');
  }, []);
  const options = useMemo(() => [{
    label: /*#__PURE__*/React.createElement("div", {
      className: classes.option
    }, /*#__PURE__*/React.createElement(Text, null, t('label_companyname')), /*#__PURE__*/React.createElement(Popover, _extends({}, popoverDefaultProps, {
      content: getPopoverContent({
        id: ADVANCED_IMPORT_IDENTIFIERS.name,
        title: t('label_companyname'),
        text: t('cfm_label_entity_name_desc')
      }),
      visible: popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.name
    }), /*#__PURE__*/React.createElement(Icon, {
      name: "info",
      color: theme.colors.grey[500],
      onMouseOver: onMouseOverOrOut(ADVANCED_IMPORT_IDENTIFIERS.name),
      onMouseOut: onMouseOverOrOut()
    }))),
    value: ADVANCED_IMPORT_IDENTIFIERS.name,
    className: 'import-export__import-type--update-id--name'
  }, {
    label: /*#__PURE__*/React.createElement("div", {
      className: classes.option
    }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_id_account')), /*#__PURE__*/React.createElement(Popover, _extends({}, popoverDefaultProps, {
      content: getPopoverContent({
        id: ADVANCED_IMPORT_IDENTIFIERS.id,
        title: t('cfm_label_id_account'),
        text: t('cfm_label_fm_id_desc')
      }),
      visible: popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.id
    }), /*#__PURE__*/React.createElement(Icon, {
      name: "info",
      color: theme.colors.grey[500],
      onMouseOver: onMouseOverOrOut(ADVANCED_IMPORT_IDENTIFIERS.id),
      onMouseOut: onMouseOverOrOut()
    }))),
    value: ADVANCED_IMPORT_IDENTIFIERS.id,
    className: 'import-export__import-type--update-id--id'
  }, {
    label: /*#__PURE__*/React.createElement("div", {
      className: classes.option
    }, /*#__PURE__*/React.createElement(Text, null, t('cfm_label_external_id_account')), /*#__PURE__*/React.createElement(Popover, _extends({}, popoverDefaultProps, {
      content: getPopoverContent({
        id: ADVANCED_IMPORT_IDENTIFIERS.extId,
        title: t('cfm_label_external_id_account'),
        text: t('cfm_label_customer_id_desc')
      }),
      visible: popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.extId
    }), /*#__PURE__*/React.createElement(Icon, {
      name: "info",
      color: theme.colors.grey[500],
      onMouseOver: onMouseOverOrOut(ADVANCED_IMPORT_IDENTIFIERS.extId),
      onMouseOut: onMouseOverOrOut()
    }))),
    value: ADVANCED_IMPORT_IDENTIFIERS.extId,
    className: 'import-export__import-type--update-id--extid'
  }], [t, classes.option, theme.colors, getPopoverContent, popoverDefaultProps, popoverVisible, onMouseOverOrOut]);
  const onChangeIdentifier = useCallback(id => {
    setIdentifier && setIdentifier(id);
  }, [setIdentifier]);
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(classes.root, {
      [classes.withMarginTop]: withMarginTop
    }),
    onClick: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.toggleContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.baseLeft
  }, _ref3, /*#__PURE__*/React.createElement(Text, {
    type: "subtitle",
    bold: true,
    color: isAdvanced ? theme.colors.textLight.primary : theme.colors.textLight.secondary
  }, t('cfm_label_match_accounts'))), /*#__PURE__*/React.createElement("div", {
    className: classes.baseLeft
  }, /*#__PURE__*/React.createElement(Switch, {
    checked: isAdvanced,
    onChange: onChangeAdvanced
  }), /*#__PURE__*/React.createElement(Text, {
    color: isAdvanced ? theme.colors.textLight.primary : theme.colors.textLight.secondary
  }, t('cfm_label_advanced_mode')))), isAdvanced && /*#__PURE__*/React.createElement("div", {
    className: classes.advancedContainer
  }, /*#__PURE__*/React.createElement(Advice, {
    type: "info",
    showIcon: true,
    showCollapse: false
  }, t('cfm_label_choose_identifier_desc')), /*#__PURE__*/React.createElement("div", {
    className: classes.options
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    className: classes.radioGroup,
    labelMode: "vertical",
    options: options,
    onChange: onChangeIdentifier,
    value: identifier,
    orientation: "horizontal",
    isFullWidth: true,
    overrides: {
      root: {
        style: {
          width: '100%',
          paddingBottom: '0px'
        }
      },
      radioControl: {
        style: {
          marginBottom: '0px'
        }
      }
    }
  }))));
});
export default IdentifierPicker;